import axios from 'axios';

// Use main API backend - Admin routes are at /api/admin/*
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance for Admin API
const adminClient = axios.create({
  baseURL: `${API_URL}/api/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - add auth token
adminClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
adminClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token } = response.data;
        localStorage.setItem('access_token', access_token);
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token);
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return adminClient(originalRequest);
      } catch (refreshError) {
        // Clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const adminApi = {
  // ============= Dashboard / Stats =============

  // Get system-wide statistics
  getStats: async () => {
    const response = await adminClient.get('/stats');
    return response.data;
  },

  // Get today's activity summary
  getTodayActivity: async () => {
    const response = await adminClient.get('/activity/today');
    return response.data;
  },

  // ============= User Management =============

  // List all users
  getUsers: async (skip = 0, limit = 100, search = null) => {
    const params = new URLSearchParams({ skip, limit });
    if (search) params.append('search', search);
    const response = await adminClient.get(`/users?${params}`);
    return response.data;
  },

  // Get user details with accounts
  getUserDetails: async (userId) => {
    const response = await adminClient.get(`/users/${userId}`);
    return response.data;
  },

  // Update user (active status, admin role)
  updateUser: async (userId, data) => {
    const response = await adminClient.patch(`/users/${userId}`, data);
    return response.data;
  },

  // Delete user
  deleteUser: async (userId) => {
    const response = await adminClient.delete(`/users/${userId}`);
    return response.data;
  },

  // ============= Account Management =============

  // List all broker accounts
  getAccounts: async (skip = 0, limit = 100, broker = null, status = null) => {
    const params = new URLSearchParams({ skip, limit });
    if (broker) params.append('broker', broker);
    if (status) params.append('status', status);
    const response = await adminClient.get(`/accounts?${params}`);
    return response.data;
  },

  // Login account (admin override)
  loginAccount: async (accountId) => {
    const response = await adminClient.post(`/accounts/${accountId}/login`);
    return response.data;
  },

  // Update account proxy
  updateAccountProxy: async (accountId, proxyUrl) => {
    const response = await adminClient.patch(`/accounts/${accountId}/proxy`, null, {
      params: { proxy_url: proxyUrl }
    });
    return response.data;
  },

  // ============= Proxy Management =============

  // List all proxy configurations
  getProxies: async () => {
    const response = await adminClient.get('/proxies');
    return response.data;
  },

  // Bulk assign proxies
  bulkAssignProxies: async (assignments) => {
    const response = await adminClient.post('/proxies/bulk-assign', { assignments });
    return response.data;
  },

  // ============= Logs =============

  // Get all logs
  getLogs: async (limit = 100, logType = null, userId = null) => {
    const params = new URLSearchParams({ limit });
    if (logType) params.append('log_type', logType);
    if (userId) params.append('user_id', userId);
    const response = await adminClient.get(`/logs?${params}`);
    return response.data;
  },
};

export default adminApi;
